const USER_POOL_ID = 'us-east-1_ImI1bvmVS'
const REGION = USER_POOL_ID.split('_')[0]
const DEFAULT_CLIENT_ID = '5doo9h4vqutnrbgani23emmn7n'

function getClientId() {
  return (
    process.env.VUE_APP_COGNITO_CLIENT_ID ||
    (typeof window !== 'undefined' && window.__WALL_LABS_COGNITO_CLIENT_ID__) ||
    DEFAULT_CLIENT_ID
  )
}

function parseCognitoError(data, fallbackMessage) {
  return (
    data?.message ||
    data?.Message ||
    (data?.__type ? String(data.__type).replace(/^.*#/, '') : '') ||
    fallbackMessage
  )
}

async function cognitoFetch(target, payload, fallbackMessage) {
  const endpoint = `https://cognito-idp.${REGION}.amazonaws.com/`

  let response
  try {
    response = await fetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-amz-json-1.1',
        'X-Amz-Target': target
      },
      body: JSON.stringify(payload)
    })
  } catch (error) {
    const message = String(error?.message || '').toLowerCase()
    if (
      message.includes('failed to fetch') ||
      message.includes('networkerror') ||
      message.includes('load failed')
    ) {
      throw new Error(
        'Unable to reach authentication service. Check your network connection and try again.'
      )
    }
    throw error
  }

  const data = await response.json()
  if (!response.ok) {
    throw new Error(parseCognitoError(data, fallbackMessage))
  }

  return data
}

export async function loginWithCognito(username, password) {
  const clientId = getClientId()

  if (!clientId) {
    throw new Error(
      'Missing Cognito App Client ID.'
    )
  }

  const data = await cognitoFetch(
    'AWSCognitoIdentityProviderService.InitiateAuth',
    {
      AuthFlow: 'USER_PASSWORD_AUTH',
      ClientId: clientId,
      AuthParameters: {
        USERNAME: username,
        PASSWORD: password
      }
    },
    'Authentication failed.'
  )

  return {
    userPoolId: USER_POOL_ID,
    region: REGION,
    authResult: data.AuthenticationResult || null,
    challengeName: data.ChallengeName || null,
    session: data.Session || null
  }
}

export async function completeNewPasswordChallenge({
  username,
  newPassword,
  session
}) {
  const clientId = getClientId()

  if (!clientId) {
    throw new Error('Missing Cognito App Client ID.')
  }

  if (!session) {
    throw new Error('Missing Cognito challenge session.')
  }

  const data = await cognitoFetch(
    'AWSCognitoIdentityProviderService.RespondToAuthChallenge',
    {
      ChallengeName: 'NEW_PASSWORD_REQUIRED',
      ClientId: clientId,
      Session: session,
      ChallengeResponses: {
        USERNAME: username,
        NEW_PASSWORD: newPassword
      }
    },
    'Failed to set new password.'
  )

  return {
    authResult: data.AuthenticationResult || null,
    challengeName: data.ChallengeName || null,
    session: data.Session || null
  }
}

export async function requestPasswordReset(username) {
  const clientId = getClientId()

  if (!clientId) {
    throw new Error('Missing Cognito App Client ID.')
  }

  const data = await cognitoFetch(
    'AWSCognitoIdentityProviderService.ForgotPassword',
    {
      ClientId: clientId,
      Username: username
    },
    'Failed to send reset code.'
  )

  return {
    codeDeliveryDetails: data.CodeDeliveryDetails || null
  }
}

export async function confirmPasswordReset({
  username,
  confirmationCode,
  newPassword
}) {
  const clientId = getClientId()

  if (!clientId) {
    throw new Error('Missing Cognito App Client ID.')
  }

  await cognitoFetch(
    'AWSCognitoIdentityProviderService.ConfirmForgotPassword',
    {
      ClientId: clientId,
      Username: username,
      ConfirmationCode: confirmationCode,
      Password: newPassword
    },
    'Failed to reset password.'
  )

  return { success: true }
}

export { USER_POOL_ID, REGION }
